import { Info } from 'lucide-react';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { EmissionScope } from '../../types/emissions';
import {
  type FormData,
  REGION_OPTIONS,
  SCOPE1_ACTIVITY_TYPES,
  SCOPE2_ACTIVITY_TYPES,
} from './types';

interface SelectOption {
  value: string;
  label: string;
}

interface ReviewStepProps {
  formData: FormData;
  unitOptions: SelectOption[];
  vehicleTypeOptions: SelectOption[];
  fuelTypeOptions: SelectOption[];
  refrigerantTypeOptions: SelectOption[];
  processTypeOptions: SelectOption[];
}

export const ReviewStep = ({
  formData,
  unitOptions,
  vehicleTypeOptions,
  fuelTypeOptions,
  refrigerantTypeOptions,
  processTypeOptions,
}: ReviewStepProps) => {
  const activityTypeOptions = [...SCOPE1_ACTIVITY_TYPES, ...SCOPE2_ACTIVITY_TYPES];

  return (
    <Card className='tw:mb-6'>
      <CardContent className='tw:p-6'>
        <h5 className='tw:font-semibold tw:mb-2'>Review Activity</h5>
        <p className='tw:text-sm tw:text-muted-foreground tw:mb-4'>
          Review the details below before submitting. The emission calculation will be performed automatically.
        </p>

        <div className='tw:rounded-lg tw:bg-muted/50 tw:p-4 tw:mb-4'>
          <table className='tw:w-full tw:text-sm'>
            <tbody className='tw:divide-y'>
              {formData.name && (
                <tr>
                  <td className='tw:py-2 tw:text-muted-foreground tw:font-medium tw:w-2/5'>Name</td>
                  <td className='tw:py-2'>{formData.name}</td>
                </tr>
              )}
              <tr>
                <td className='tw:py-2 tw:text-muted-foreground tw:font-medium tw:w-2/5'>Scope</td>
                <td className='tw:py-2'>
                  {formData.scope === EmissionScope.Scope1
                    ? 'Scope 1 - Direct Emissions'
                    : 'Scope 2 - Purchased Energy'}
                </td>
              </tr>
              <tr>
                <td className='tw:py-2 tw:text-muted-foreground tw:font-medium'>Activity Type</td>
                <td className='tw:py-2'>
                  {activityTypeOptions.find((o) => o.value === formData.activityType)?.label}
                </td>
              </tr>
              <tr>
                <td className='tw:py-2 tw:text-muted-foreground tw:font-medium'>Period</td>
                <td className='tw:py-2'>
                  {formData.periodStart} to {formData.periodEnd}
                </td>
              </tr>
              {formData.vehicleType && (
                <tr>
                  <td className='tw:py-2 tw:text-muted-foreground tw:font-medium'>Vehicle Type</td>
                  <td className='tw:py-2'>
                    {vehicleTypeOptions.find((o) => o.value === formData.vehicleType)?.label}
                  </td>
                </tr>
              )}
              {formData.fuelType && (
                <tr>
                  <td className='tw:py-2 tw:text-muted-foreground tw:font-medium'>Fuel Type</td>
                  <td className='tw:py-2'>
                    {fuelTypeOptions.find((o) => o.value === formData.fuelType)?.label}
                  </td>
                </tr>
              )}
              {formData.refrigerantType && (
                <tr>
                  <td className='tw:py-2 tw:text-muted-foreground tw:font-medium'>Gas / Refrigerant Type</td>
                  <td className='tw:py-2'>
                    {refrigerantTypeOptions.find((o) => o.value === formData.refrigerantType)?.label}
                  </td>
                </tr>
              )}
              {formData.processType && (
                <tr>
                  <td className='tw:py-2 tw:text-muted-foreground tw:font-medium'>Process Type</td>
                  <td className='tw:py-2'>
                    {processTypeOptions.find((o) => o.value === formData.processType)?.label}
                  </td>
                </tr>
              )}
              <tr>
                <td className='tw:py-2 tw:text-muted-foreground tw:font-medium'>Quantity</td>
                <td className='tw:py-2'>
                  {parseFloat(formData.quantity).toLocaleString()}{' '}
                  {unitOptions.find((o) => o.value === formData.unit)?.label}
                </td>
              </tr>
              <tr>
                <td className='tw:py-2 tw:text-muted-foreground tw:font-medium'>Region</td>
                <td className='tw:py-2'>{REGION_OPTIONS.find((o) => o.value === formData.region)?.label}</td>
              </tr>
              <tr>
                <td className='tw:py-2 tw:text-muted-foreground tw:font-medium'>Factor Fallback</td>
                <td className='tw:py-2'>
                  {formData.regionFallback && formData.yearFallback
                    ? 'Region + Year'
                    : formData.regionFallback
                      ? 'Region only'
                      : formData.yearFallback
                        ? 'Year only'
                        : 'None'}
                </td>
              </tr>
              {formData.dataSource && (
                <tr>
                  <td className='tw:py-2 tw:text-muted-foreground tw:font-medium'>Data Source</td>
                  <td className='tw:py-2'>{formData.dataSource}</td>
                </tr>
              )}
              {formData.notes && (
                <tr>
                  <td className='tw:py-2 tw:text-muted-foreground tw:font-medium'>Notes</td>
                  <td className='tw:py-2'>{formData.notes}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className='tw:flex tw:gap-3 tw:rounded-lg tw:border tw:border-emerald-200 tw:bg-emerald-50 tw:p-3 tw:text-emerald-800 tw:text-sm'>
          <Info className='tw:size-4 tw:shrink-0 tw:mt-0.5' />
          Once submitted, the system will automatically calculate the CO2e emissions using the
          appropriate emission factors for your region and activity type.
        </div>
      </CardContent>
    </Card>
  );
};
